import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const CALENDLY_URL =
  'https://calendly.com/mark-empathos/let-s-talk-about-trust-personal-chief-trust-officer';
const CALENDLY_BOOKED_KEY = 'empathos_talk_about_trust_booked';

function hasBookedTalkSession() {
  try {
    return sessionStorage.getItem(CALENDLY_BOOKED_KEY) === '1';
  } catch {
    return false;
  }
}

function markTalkSessionBooked() {
  try {
    sessionStorage.setItem(CALENDLY_BOOKED_KEY, '1');
  } catch {
    // Ignore storage failures (private mode, etc.)
  }
}

function closeCalendlyPopup() {
  document.querySelectorAll('.calendly-overlay').forEach((node) => node.remove());
}

function isCalendlyMessage(event) {
  return event.origin === 'https://calendly.com' && typeof event.data?.event === 'string';
}

function ensureCalendlyAssets() {
  return new Promise((resolve, reject) => {
    if (window.Calendly) {
      resolve(window.Calendly);
      return;
    }

    if (!document.querySelector('link[data-calendly-widget-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      link.setAttribute('data-calendly-widget-css', 'true');
      document.head.appendChild(link);
    }

    const existingScript = document.querySelector('script[data-calendly-widget-js]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.Calendly));
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.setAttribute('data-calendly-widget-js', 'true');
    script.onload = () => resolve(window.Calendly);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function ContactViewTags({ activeView, onViewChange }) {
  return (
    <div className="contact-view-tags">
      <button
        type="button"
        className={`section-tag ${activeView === 'contact' ? 'section-tag--active' : 'section-tag--inactive'}`}
        onClick={() => onViewChange('contact')}
      >
        Get In Touch
      </button>
      <button
        type="button"
        className={`section-tag ${activeView === 'talk-about-trust' ? 'section-tag--active' : 'section-tag--inactive'}`}
        onClick={() => onViewChange('talk-about-trust')}
      >
        Talk About Trust
      </button>
    </div>
  );
}

function Contact() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView =
    searchParams.get('view') === 'talk-about-trust' ? 'talk-about-trust' : 'contact';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [hasBookedSession, setHasBookedSession] = useState(() => hasBookedTalkSession());

  // Prefetch Calendly assets when the Talk About Trust view is shown
  useEffect(() => {
    if (activeView !== 'talk-about-trust' || hasBookedSession) return undefined;
    ensureCalendlyAssets().catch((error) => {
      console.error('Failed to load Calendly widget:', error);
    });
    return undefined;
  }, [activeView, hasBookedSession]);

  // Calendly posts calendly.event_scheduled when a booking completes in the embed
  useEffect(() => {
    const handleMessage = (event) => {
      if (!isCalendlyMessage(event)) return;
      if (event.data.event !== 'calendly.event_scheduled') return;

      markTalkSessionBooked();
      setHasBookedSession(true);
      closeCalendlyPopup();
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const scrollToContent = () => {
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleViewChange = (view) => {
    if (view === 'talk-about-trust') {
      setSearchParams({ view: 'talk-about-trust' });
    } else {
      setSearchParams({});
    }
    setTimeout(scrollToContent, 100);
  };

  const openCalendlyPopup = useCallback(async () => {
    if (hasBookedTalkSession()) {
      setHasBookedSession(true);
      return;
    }

    try {
      const Calendly = await ensureCalendlyAssets();
      if (Calendly?.initPopupWidget) {
        Calendly.initPopupWidget({ url: CALENDLY_URL });
      } else {
        window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Unable to open Calendly popup:', error);
      window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // API Gateway endpoint for contact form
      const apiUrl = process.env.REACT_APP_CONTACT_API_URL || 'https://rnbcs16jm8.execute-api.ap-southeast-2.amazonaws.com/prod/contact';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: result.message || 'Message sent successfully! One of our Chief Trust Officers will be in contact shortly.'
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.error || 'Failed to send message. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Contact Header Section */}
      <section
        className={`contact-intro-section${activeView === 'talk-about-trust' ? ' contact-intro-section--talk' : ''}`}
      >
        <div className="section-header">
          <ContactViewTags activeView={activeView} onViewChange={handleViewChange} />
          {activeView === 'talk-about-trust' ? (
            <>
              <h1>Let&rsquo;s Talk About Trust</h1>
              <p className="talk-trust-subtitle">
                One hour with your personal Chief Trust Officer
              </p>
            </>
          ) : (
            <h1>Contact Your Trust Guides</h1>
          )}
        </div>

        {activeView === 'contact' && (
          <div className="contact-intro-content">
            <p>
              Ready to close the trust gap? Our Trust Guides will help you measure, grow, and nurture your Trust Intelligence<sup>&trade;</sup> so your leadership, your team, and your strategy succeed.
            </p>
            <h2 className="centered-heading">It starts with one step. Let&rsquo;s talk about your Trust Intelligence<sup>&trade;</sup></h2>
          </div>
        )}
      </section>

      {activeView === 'contact' ? (
        /* Contact Details & Form Section */
        <section className="contact-main-section" id="contact-form">
          <div className="contact-container">
            <div className="contact-info">
              <h2>Connect With Us</h2>
              <p>
                Whether you&rsquo;re looking to measure your Trust Intelligence<sup>&trade;</sup>, develop personalized advisory strategies,
                or transform your entire team, we&rsquo;re here to guide you on your journey.
              </p>

              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#2B9BCA" strokeWidth="2" fill="none"/>
                      <polyline points="22,6 12,13 2,6" stroke="#2B9BCA" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <div className="contact-text">
                    <h3>Email</h3>
                    <p>team@empathos.com.au</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="#2B9BCA" strokeWidth="2" fill="none"/>
                      <circle cx="12" cy="10" r="3" stroke="#2B9BCA" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <div className="contact-text">
                    <h3>Location</h3>
                    <p>Sydney, Australia<br />Serving leaders globally</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ color: '#2B9BCA' }}>
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </div>
                  <div className="contact-text">
                    <h3>LinkedIn</h3>
                    <p>
                      <a
                        href="https://www.linkedin.com/company/empathostrustintelligence/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-link"
                      >
                        @empathostrustintelligence
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-section">
              <h2>Send Us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your leadership challenges and how we can help you build trust intelligence™..."
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="cta-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus && (
                  <div className={`submit-status ${submitStatus.type}`}>
                    {submitStatus.message}
                  </div>
                )}

                <p className="form-note">
                  One of our Chief Trust Officers will be in contact shortly to discuss
                  how we can help you restore, build, and sustain trust in your leadership.
                </p>
              </form>
            </div>
          </div>
        </section>
      ) : (
        <section className="talk-trust-section" id="talk-about-trust">
          <div className="talk-trust-content">
            <p className="talk-trust-lead">
              <strong>You can feel that something isn&rsquo;t right. But can you see where trust is breaking down?</strong>
            </p>
            <p>
              Perhaps people are holding back, avoiding difficult conversations or failing to follow through.
              You can feel the impact, but the cause remains unclear.
            </p>

            <div className="talk-trust-mark">
              <div className="talk-trust-mark-image member-image member-image--mark">
                <img src="/Incubator_Headshots_26thFeb26-61.jpg" alt="Mark Panayiotis Notaras" />
              </div>
              <div className="talk-trust-mark-copy">
                <p className="talk-trust-lead">
                  <strong>Meet Mark Panayiotis Notaras, your personal Chief Trust Officer.</strong>
                </p>
                <p>
                  In one focused hour, Mark will help you explore the trust dynamics affecting your team or partnership.
                  Drawing on Trust Intelligence<sup>&trade;</sup>, you&rsquo;ll examine where trust may be weakening,
                  what is contributing to the gap and how you can begin strengthening it.
                </p>
              </div>
            </div>

            <div className="talk-trust-outcomes">
              <h3>You&rsquo;ll leave with:</h3>
              <ul>
                <li>A clearer understanding of where trust may be breaking down</li>
                <li>Insight into how it is affecting your relationships and performance</li>
                <li>Practical trust-building actions to take next</li>
              </ul>
            </div>

            <p className="talk-trust-summary">
              <strong>One hour. Clear insight. Practical next steps.</strong>
            </p>
            <p className="talk-trust-pricing">
              AUD $250. Payment is due after your session.
            </p>

            {hasBookedSession ? (
              <div className="talk-trust-booked" role="status">
                <p>
                  <strong>You&rsquo;re booked.</strong> Thanks - Mark will see you at your scheduled session.
                  A confirmation email should arrive from Calendly shortly.
                </p>
              </div>
            ) : (
              <button
                type="button"
                className="cta-primary large"
                onClick={openCalendlyPopup}
              >
                Book your session with Mark
              </button>
            )}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-section">
              <h4>Navigation</h4>
              <nav className="footer-nav">
                <Link to="/" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Home</Link>
                <Link to="/about" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>About</Link>
                <Link to="/contact" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Contact</Link>
                <Link to="/login" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Login</Link>
              </nav>
            </div>

            <div className="footer-section">
              <h4>Connect</h4>
              <div className="social-links">
                <a href="https://www.linkedin.com/company/empathostrustintelligence/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
                  </svg>
                  LinkedIn
                </a>
                <a href="mailto:team@empathos.com.au" target="_blank" rel="noopener noreferrer" aria-label="Email">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                  Email
                </a>
              </div>
            </div>

            <div className="footer-section">
              <h4>Trust Intelligence<sup>&trade;</sup></h4>
              <div className="definition">
                <div className="definition-term">
                  /trʌst ɪnˈtelɪdʒəns/
                </div>
                <div className="definition-part">
                  <em>noun</em>
                </div>
                <div className="definition-meaning">
                  The measurable capacity to understand and strengthen trust between leaders and their teams.
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="copyright">
            <p><em>We acknowledge the lands of the Bidjigal People of the Eora Nation upon whose land we work.</em></p>
              <p>&copy; {new Date().getFullYear()} Empathos. All rights reserved.</p>
            </div>
            <div className="footer-logo">
              <a href="/" aria-label="Return to home">
                <img src="/empy_svg_test.svg" alt="Empathos" className="footer-logo-svg" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Contact;
